import type { InvoiceResponse } from '@palaccount/contracts';
import { CheckCheck, Pencil, Receipt, RotateCcw, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount } from '@/components/amount';
import {
  BulkExportButton,
  BulkPdfButton,
  BulkPostButton,
  DocumentListPage,
  DocumentPdfButton,
  documentLocale,
  normalisePaged,
  type DocumentListConfig,
} from '@/components/document-list';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { PastDueChip, StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { daysBetween } from '@/lib/dates';
import { percentOf, subtractAmounts } from '@/lib/money';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause, throwApiError } from '@/lib/server-messages';
import { useCompanyToday } from '@/lib/session';

import { fetchInvoicesPage, INVOICES_KEY, useCancelInvoice, useInvoice, useInvoices, useInvoiceStats, usePostInvoice } from './api';
import { invoiceSteps } from './sales-steppers';
import { SalesTabs } from './sales-tabs';

const STATUSES: InvoiceResponse['status'][] = ['draft', 'posted', 'cancelled'];
/** SAL-00 §7 through the one palette: Draft grey, Posted blue, Cancelled red-faded. */
const TONES = { draft: 'draft', posted: 'posted', cancelled: 'cancelled' } as const;

type InvoiceStats = ReturnType<typeof useInvoiceStats>['data'] & object;

/**
 * SAL-04's list on the shared list-and-detail page (wave 3B). Drafts appear
 * HERE and nowhere else (BR-SAL-00-27). The tiles are the register's — the
 * four figures `/api/sales/invoices/stats` answers, in the company's base
 * currency, which the response names so no sign is ever hard-coded (GEN-107).
 * The selected invoice shows its place in the order-to-cash chain (the order
 * and delivery notes the wave-3B row now carries; unknown stages greyed as
 * skipped), how much of it is paid and how late it is, and the acts a row
 * can take: post a draft, record a receipt against a posted one, raise a
 * credit note, print it, cancel it (ruling A6, reason required — GEN-72).
 * BR-MD-09-09's Past Due chip rides beside the status chip exactly as it did
 * on the table.
 */
export function InvoicesListPage() {
  const { t, i18n } = useTranslation();
  const today = useCompanyToday();
  const locale = documentLocale(i18n.language);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  /** The selection lives in the URL (the shared page's deep link); the mutations key on it. */
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const postInvoice = usePostInvoice(selectedId);
  const cancelInvoice = useCancelInvoice(selectedId);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.approve');
      setError(localizeServerCause(t, thrown, 'doclist.actFailed'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (status: InvoiceResponse['status']) => t(`common.status.${status}`);
  const chip = (invoice: InvoiceResponse) => (
    <span className="inline-flex gap-1">
      <StatusChip status={invoice.status} tones={TONES} ns="common" />
      <PastDueChip level={invoice.pastDue} />
    </span>
  );
  const exportColumns = [
    { header: t('sales.colNo'), value: (i: InvoiceResponse) => i.code },
    { header: t('common.date'), value: (i: InvoiceResponse) => i.invoiceDate, kind: 'date' as const },
    { header: t('sales.fDueDate'), value: (i: InvoiceResponse) => i.dueDate, kind: 'date' as const },
    { header: t('sales.colCustomer'), value: (i: InvoiceResponse) => i.partnerName },
    { header: t('sales.colStatus'), value: (i: InvoiceResponse) => statusLabel(i.status) },
    { header: t('sales.exportColCurrency'), value: (i: InvoiceResponse) => i.currency },
    { header: t('sales.colGrandTotal'), value: (i: InvoiceResponse) => i.grandTotal, kind: 'number' as const },
    { header: t('sales.colBalanceDue'), value: (i: InvoiceResponse) => i.balanceDue, kind: 'number' as const },
  ];
  const pdfRequest = (invoice: InvoiceResponse) => ({ kind: 'sales_invoice' as const, locale, invoiceId: invoice.id });

  const config: DocumentListConfig<InvoiceResponse, InvoiceResponse['status'], InvoiceStats> = {
    titleKey: 'sales.title',
    tabs: <SalesTabs />,
    newAction: { to: '/sales/new', labelKey: 'common.new', permission: 'sales.create', reasonKey: 'perm.cannot.createInvoices' },
    statuses: STATUSES,
    statusLabel,
    /* G-11: number, customer or the per-customer reference — the contract takes `q`. */
    search: { labelKey: 'sales.searchLabel', placeholderKey: 'sales.searchPlaceholder' },
    dateRange: true,
    partner: { kind: 'customer' },
    useList: (filters, page, pageSize) => {
      const query = useInvoices(
        {
          status: filters.status || undefined,
          q: filters.q || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          partnerId: filters.partnerId || undefined,
        },
        page,
        pageSize,
      );
      return { data: normalisePaged(query.data, 'invoices'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => useInvoice(id ?? undefined),
    kpis: {
      useStats: () => {
        const stats = useInvoiceStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      /* Money tiles are in the base the response NAMES (GEN-107) — never a guessed sign. */
      tiles: [
        { key: 'outstanding', labelKey: 'sales.tileOutstanding', format: 'money', value: (s) => s.outstanding, currency: (s) => s.currency },
        { key: 'overdueCount', labelKey: 'sales.tileOverdueCount', format: 'count', value: (s) => s.overdueCount, tone: 'warn' },
        { key: 'overdueValue', labelKey: 'sales.tileOverdueValue', format: 'money', value: (s) => s.overdueValue, currency: (s) => s.currency, tone: 'warn' },
        { key: 'drafts', labelKey: 'sales.tileDrafts', format: 'count', value: (s) => s.draftCount },
        { key: 'collected', labelKey: 'sales.tileCollected', format: 'money', value: (s) => s.collectedThisMonth, currency: (s) => s.currency, tone: 'pos' },
      ],
    },
    card: (invoice) => ({
      code: invoice.code,
      chip: chip(invoice),
      title: invoice.partnerName,
      meta: <bdi dir="ltr">{invoice.invoiceDate}</bdi>,
      amount: { value: invoice.grandTotal, currency: invoice.currency },
    }),
    columns: { titleKey: 'sales.colCustomer', metaKey: 'common.date', amountKey: 'sales.colGrandTotal', statusKey: 'sales.colStatus' },
    emptyKey: 'sales.empty',
    selectOneKey: 'sales.selectOne',
    notice: error ? (
      <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    ) : null,
    export: {
      filename: 'invoices',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) =>
        fetchInvoicesPage(
          {
            status: filters.status || undefined,
            q: filters.q || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
            partnerId: filters.partnerId || undefined,
          },
          page,
          pageSize,
        ),
    },
    bulkActions: (rows, clear) => (
      <>
        <BulkPostButton
          rows={rows}
          permission="sales.approve"
          reasonKey="perm.cannot.postInvoices"
          invalidate={[INVOICES_KEY]}
          clear={clear}
          post={async (row) => {
            const response = await api.invoices.post({ params: { invoiceId: row.id }, body: {} });
            if (response.status !== 200) throwApiError(response.status, response.body);
          }}
        />
        <BulkPdfButton requests={rows.map(pdfRequest)} />
        <BulkExportButton filename="invoices" columns={exportColumns} rows={rows} />
      </>
    ),
    detail: {
      chip,
      links: (invoice) => [
        ...(invoice.salesOrderCode
          ? [
              {
                key: 'order',
                to: `/sales/orders?selected=${invoice.salesOrderId}`,
                tone: 'muted' as const,
                label: t('sales.fromOrder', { code: invoice.salesOrderCode }),
              },
            ]
          : []),
        ...invoice.deliveryNoteCodes.map((code) => ({
          key: `dn-${code}`,
          to: `/sales/deliveries`,
          tone: 'muted' as const,
          label: <bdi dir="ltr">{code}</bdi>,
        })),
      ],
      subline: (invoice) => (
        <>
          {invoice.partnerName} · {t('sales.fDueDate')} <bdi dir="ltr">{invoice.dueDate}</bdi>
        </>
      ),
      steps: { labelKey: 'orders.stepper', of: invoiceSteps },
      /* "% paid · days overdue" — paid is what has settled (receipts and standing credits), overdue only while posted and unpaid. */
      progress: (invoice) => {
        if (invoice.status === 'draft') return null;
        const paid = percentOf(subtractAmounts(invoice.grandTotal, invoice.balanceDue), invoice.grandTotal);
        const overdue = invoice.status === 'posted' && !invoice.fullyPaid ? daysBetween(invoice.dueDate, today) : 0;
        return overdue > 0 ? t('sales.listProgressOverdue', { paid, days: overdue }) : t('sales.listProgress', { paid });
      },
      actions: (invoice) => (
        <>
          <Button asChild variant="outline">
            <Link to={`/sales/${invoice.id}`}>
              <Pencil aria-hidden /> {invoice.status === 'draft' ? t('common.edit') : t('doclist.open')}
            </Link>
          </Button>
          {invoice.status === 'draft' ? (
            <Gated permission="sales.approve" reasonKey="perm.cannot.postInvoices">
              <Button onClick={() => void act(() => postInvoice.mutateAsync())} disabled={busy}>
                <CheckCheck aria-hidden /> {t('sales.actPost')}
              </Button>
            </Gated>
          ) : null}
          {invoice.status === 'posted' && !invoice.fullyPaid ? (
            <Gated permission="accounting.create" reasonKey="perm.cannot.createReceiptVouchers">
              <Button asChild variant="outline">
                <Link to={`/accounting/receipts/new?partnerId=${invoice.partnerId}&invoiceId=${invoice.id}&currency=${invoice.currency}`}>
                  <Receipt aria-hidden /> {t('sales.actRecordPayment')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          {invoice.status === 'posted' ? (
            <Gated permission="sales.create" reasonKey="perm.cannot.createSalesReturns">
              <Button asChild variant="outline">
                <Link to={`/sales/returns/new?invoiceId=${invoice.id}`}>
                  <RotateCcw aria-hidden /> {t('sales.actCreditNote')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          <DocumentPdfButton request={pdfRequest(invoice)} />
          {invoice.status === 'posted' ? (
            <Gated permission="sales.approve" reasonKey="perm.cannot.cancelInvoices">
              <Button variant="destructive" onClick={() => setCancelAsk(true)} disabled={busy}>
                <XCircle aria-hidden /> {t('sales.actCancel')}
              </Button>
            </Gated>
          ) : null}
          {invoice.status === 'posted' ? (
            <ReasonBox
              open={cancelAsk}
              title={t('sales.listCancelTitle')}
              prompt={t('sales.listCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => {
                setCancelAsk(false);
                void act(() => cancelInvoice.mutateAsync(reason));
              }}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </>
      ),
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'qty', labelKey: 'sales.colQty', align: 'end', className: 'w-24' },
          { key: 'unitPrice', labelKey: 'sales.colUnitPrice', align: 'end', className: 'w-32' },
          { key: 'total', labelKey: 'sales.colTotal', align: 'end', className: 'w-32' },
        ],
        rows: (invoice) =>
          invoice.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <bdi key="q" dir="ltr" className="font-mono">{line.qty.replace(/\.?0+$/, '')}</bdi>,
              <Amount key="u" value={line.unitPrice} currency={invoice.currency} />,
              <Amount key="t" value={line.lineTotal} currency={invoice.currency} />,
            ],
          })),
      },
      totals: (invoice) => ({
        labelKey: 'orders.netTaxTotal',
        amounts: [
          { value: invoice.subtotal, currency: invoice.currency },
          { value: invoice.taxAmount, currency: invoice.currency },
          { value: invoice.grandTotal, currency: invoice.currency, emphasis: true },
        ],
      }),
    },
  };

  return <DocumentListPage config={config} />;
}
